/**
 * AdminPanel - Painel de Atualização de Cotações
 * 
 * Princípios do Tripé:
 * ⚖️ JUSTIÇA - Admin valida dados antes de publicar
 * 🔒 SEGURANÇA - Controle total sobre os dados
 * ✨ SIMPLICIDADE - Interface fácil de usar
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useTranslation } from '../../translations';

const AdminPanel = ({ onClose, onUpdate }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  
  // Campos editáveis - os 4 principais
  const [formData, setFormData] = useState({
    preco_importacao: { value: '', reference_date: '' },
    geb10_apabor: { value: '', reference_date: '' },
    coagulo: { value: '', reference_date: '' },
    internacional: { value: '', reference_date: '' },
    cambio: { value: '', reference_date: '' },
  });

  useEffect(() => {
    fetchCurrentQuotations();
  }, []);

  const fetchCurrentQuotations = async () => {
    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/market/quotations`);
      if (response.ok) {
        const data = await response.json();
        const newFormData = { ...formData };
        data.quotations.forEach(q => {
          if (newFormData[q.type]) {
            newFormData[q.type] = {
              value: q.value?.toString() || '',
              reference_date: q.reference_date || ''
            };
          }
        });
        setFormData(newFormData);
      }
    } catch (error) {
      console.error('Erro ao carregar cotações:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (type, field, value) => {
    setFormData(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value
      }
    }));
  };

  const handleSave = async (type) => {
    setSaving(true);
    setMessage(null);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const quotationData = {
        type,
        value: parseFloat(formData[type].value),
        unit: type === 'internacional' ? 'US$/kg' : type === 'cambio' ? 'R$/US$' : 'R$/kg',
        source: getSourceName(type),
        reference_date: formData[type].reference_date,
      };

      const response = await fetch(`${API_URL}/api/market/quotations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(quotationData)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: `✅ ${getLabel(type)} ${t('updatedSuccessfully')}` });
        if (onUpdate) onUpdate();
      } else {
        setMessage({ type: 'error', text: `❌ ${t('errorSavingTryAgain')}` });
      }
    } catch (error) {
      setMessage({ type: 'error', text: `❌ ${t('connectionErrorTryAgain')}` });
    } finally {
      setSaving(false);
    }
  };

  const getLabel = (type) => {
    const labels = {
      preco_importacao: t('importPriceIEA'),
      geb10_apabor: t('geb10Apabor'),
      coagulo: t('coaguloDRC53'),
      internacional: t('smr20International'),
      cambio: t('dollarExchange'),
    };
    return labels[type] || type;
  };

  const getSourceName = (type) => {
    const sources = {
      preco_importacao: 'IEA/CNA (Admin)',
      geb10_apabor: 'APABOR (Admin)',
      coagulo: 'IEA/Notícias Agrícolas (Admin)',
      internacional: 'Trading Economics (Admin)',
      cambio: 'PTAX (Admin)',
    };
    return sources[type] || 'Admin';
  };

  const getPlaceholder = (type) => {
    const placeholders = {
      preco_importacao: 'Ex: 13.91',
      geb10_apabor: 'Ex: 12.01',
      coagulo: 'Ex: 3.76',
      internacional: 'Ex: 1.99',
      cambio: 'Ex: 5.21',
    };
    return placeholders[type] || 'Ex: 10.00';
  };

  if (loading) {
    return (
      <div 
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <Card className="w-full max-w-lg mx-4">
          <CardContent className="py-8 text-center">
            <span className="animate-spin text-3xl">⏳</span>
            <p className="mt-2 text-gray-600">{t('loadingLabel')}</p>
            <button
              onClick={onClose}
              className="mt-4 px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Fechar
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="admin-panel-overlay"
    >
      <Card className="w-full max-w-2xl mx-4 mt-8 mb-8 max-h-[90vh] flex flex-col">
        <CardHeader className="border-b bg-green-50 sticky top-0 z-10 flex-shrink-0">
          <div className="flex items-center justify-between">
            <CardTitle className="text-green-800 flex items-center gap-2">
              <span className="text-2xl">⚙️</span>
              {t('updateQuotationsTitle')}
            </CardTitle>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-200 hover:bg-red-100 text-gray-600 hover:text-red-600 text-xl font-bold transition-colors"
              data-testid="admin-panel-close"
              title="Fechar"
            >
              ✕
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            {t('updateValuesFromSources')}
          </p>
        </CardHeader>
        
        <CardContent className="py-4 space-y-4 overflow-y-auto flex-1">
          {message && (
            <div className={`p-3 rounded-lg ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {message.text}
            </div>
          )}

          {/* Campos de edição */}
          {Object.keys(formData).map((type) => (
            <div key={type} className="p-4 border rounded-lg bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <label className="font-bold text-gray-800">
                  {getLabel(type)}
                </label>
                <span className="text-xs text-gray-500">
                  {type === 'internacional' ? 'US$/kg' : type === 'cambio' ? 'R$/US$' : 'R$/kg'}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">{t('valueLabel')}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData[type].value}
                    onChange={(e) => handleChange(type, 'value', e.target.value)}
                    placeholder={getPlaceholder(type)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">{t('referenceDateLabel')}</label>
                  <input
                    type="text"
                    value={formData[type].reference_date}
                    onChange={(e) => handleChange(type, 'reference_date', e.target.value)}
                    placeholder={t('referenceDatePlaceholder')}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
              </div>
              
              <button
                onClick={() => handleSave(type)}
                disabled={saving || !formData[type].value}
                className="mt-3 w-full py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? `⏳ ${t('savingLabel')}` : `💾 ${t('saveLabel')}`}
              </button>
            </div>
          ))}

          {/* Links rápidos para fontes */}
          <div className="border-t pt-4 mt-4">
            <p className="text-sm font-medium text-gray-700 mb-2">🔗 {t('consultSourcesLabel')}</p>
            <div className="flex flex-wrap gap-2">
              <a href="http://ciagri.iea.sp.gov.br/bancodedados/borracha" target="_blank" rel="noopener noreferrer" className="text-xs px-3 py-1 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200">
                IEA - {t('importPrice')}
              </a>
              <a href="https://www.noroesteborracha.com.br/preco" target="_blank" rel="noopener noreferrer" className="text-xs px-3 py-1 bg-green-100 text-green-700 rounded-full hover:bg-green-200">
                Noroeste - GEB-10
              </a>
              <a href="https://www.noticiasagricolas.com.br/cotacoes/latex" target="_blank" rel="noopener noreferrer" className="text-xs px-3 py-1 bg-amber-100 text-amber-700 rounded-full hover:bg-amber-200">
                Notícias Agrícolas - {t('coagulum')}
              </a>
              <a href="https://tradingeconomics.com/commodity/rubber" target="_blank" rel="noopener noreferrer" className="text-xs px-3 py-1 bg-purple-100 text-purple-700 rounded-full hover:bg-purple-200">
                Trading Economics
              </a>
            </div>
          </div>

          {/* Botão fechar grande no final */}
          <button
            onClick={onClose}
            className="w-full mt-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors border border-gray-300"
            data-testid="admin-panel-close-bottom"
          >
            ✕ Fechar
          </button>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPanel;
